import { useCallback, useEffect, useMemo } from 'react';
import { FilterColumn } from '@blisspointmedia/bpm-types/dist/FilterBar';
import { Background, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { FilterBar } from '@Components/FilterBar';
import { Page } from '@Components/Page';
import { Skeleton, TableSkeleton } from '@Components/Skeleton';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { WidgetContainer } from '@Components/WidgetContainer';
import { useAppSelector } from '@src/redux';
import { useSetError } from '@src/redux/modals';
import { currentCompanySelector } from '@src/redux/company';
import { exportToExcel } from '@src/utils/download-utils';
import { useStateFunction } from '@src/utils/hooks/useData';
import {
  DataFreshnessHeaderRow,
  getDataFreshnessRowHeight,
  groupByEverythingExceptTableNames
} from './data-freshness-utils';
import { DataFreshnessRunRow, useDataFreshness } from './use-data-freshness';
import './DataFreshness.scss';

// Narrows BPMTable's `Header` (where `label`/`name` are optional) to guaranteed strings,
// since every header here always sets them - keeps the FilterBar options mapping below free
// of `label ?? ''` fallbacks.
interface DataFreshnessHeader extends TypedHeader<DataFreshnessHeaderRow> {
  label: string;
  name: string;
}

// One table name per line, rather than a single comma-joined string, so a row's height is
// exactly predictable from the count (see getDataFreshnessRowHeight).
const TableNamesCell = ({ tableNames }: { tableNames: string[] }) => (
  <div className='table-names-cell'>
    {tableNames.map(name => (
      <div key={name}>{name}</div>
    ))}
  </div>
);

const getDataFreshnessDataHeaders = (company?: string): DataFreshnessHeader[] => {
  const baseHeaders: DataFreshnessHeader[] = [
    {
      name: 'table_names',
      label: 'Table Names',
      flex: 2,
      renderer: value => {
        if (!value || !Array.isArray(value.table_names) || value.table_names.length === 0) {
          return '-';
        }
        return <TableNamesCell tableNames={value.table_names} />;
      }
    },
    {
      name: 'source',
      label: 'Source',
      flex: 1
    },
    {
      name: 'account_name',
      label: 'Account Name',
      flex: 1
    },
    {
      name: 'account_id',
      label: 'Account ID',
      flex: 1
    },
    {
      name: 'last_updated',
      label: 'Last Updated',
      flex: 1
    }
  ];

  if (!company) {
    baseHeaders.unshift({
      name: 'company',
      label: 'Company',
      flex: 1
    });
  }

  return baseHeaders;
};

interface DataFreshnessFiltersProps {
  rows: DataFreshnessRunRow[];
  options: FilterColumn[];
  onFilter: (filter: (line: DataFreshnessRunRow) => boolean) => void;
  onDownload: () => void;
  downloadDisabled: boolean;
}

const DataFreshnessFilters = ({
  rows,
  options,
  onFilter,
  onDownload,
  downloadDisabled
}: DataFreshnessFiltersProps) => (
  <div className='data-freshness-filters'>
    <FilterBar size={Size.LG} hasAdvanced onFilter={onFilter} lines={rows} options={options} />
    <div data-testid='download-dropdown'>
      <DownloadDropdown
        disabled={downloadDisabled}
        onClickOptions={[onDownload]}
        menuOptions={['XLSX']}
      />
    </div>
  </div>
);

export const DataFreshness = () => {
  const company = useAppSelector(currentCompanySelector);
  const setError = useSetError();
  const { data: dataFreshnessRows, isLoading, error } = useDataFreshness(company);

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load data",
        message: `Failed to load data freshness: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  const [filter, setFilter] = useStateFunction<(line: DataFreshnessRunRow) => boolean>(() => true);

  const headers = useMemo(() => getDataFreshnessDataHeaders(company), [company]);

  // The FilterBar filters over the raw, ungrouped rows (each with one `table_name`), not the
  // grouped display data the "Table Names" header renders (`table_names`, an array) - point
  // that one option back at the raw field so it can actually match something.
  const filterOptions = useMemo<FilterColumn[]>(
    () =>
      headers.map(({ name, label }) => ({
        name: name === 'table_names' ? 'table_name' : name,
        label
      })),
    [headers]
  );

  const groupedData = useMemo(
    () => groupByEverythingExceptTableNames(dataFreshnessRows?.filter(filter) ?? []),
    [dataFreshnessRows, filter]
  );

  const downloadExcel = useCallback(() => {
    // exportToExcel writes each field straight into a cell, so the grouped rows' `table_names`
    // array has to be flattened to a string first - an array value isn't a valid cell value.
    const flattenedData = groupedData.map(row => ({
      ...row,
      table_names: row.table_names.join(', ')
    }));
    exportToExcel(flattenedData, 'data_freshness');
  }, [groupedData]);

  // BPMTable has no loading concept of its own, so this fills its noRowsRenderer: a skeleton
  // while fetching, the company-specific "no data" prompt when nothing was fetched at all,
  // otherwise the generic empty state (e.g. the active filter matched nothing).
  const renderTableLoading = useCallback(() => {
    if (isLoading) {
      return (
        <Skeleton>
          <TableSkeleton yOffset={8} />
        </Skeleton>
      );
    }
    if (!dataFreshnessRows || dataFreshnessRows.length === 0) {
      return <TableEmptyState message={`No data available for ${company}`} />;
    }
    return <TableEmptyState />;
  }, [isLoading, dataFreshnessRows, company]);

  return (
    <Page title='Data Freshness' pageType='Data Freshness' isLoading={isLoading}>
      <div className='data-freshness-page'>
        <WidgetContainer className='data-freshness-widget' background={Background.DARK} fillHeight>
          <div className='data-freshness-table' data-testid='data-freshness-table'>
            <BPMTable
              headers={headers}
              data={groupedData}
              filterBar={false}
              emptyDisplay='-'
              rowHeightGetter={getDataFreshnessRowHeight}
              noRowsRenderer={renderTableLoading}
              additionalControls={
                <DataFreshnessFilters
                  rows={dataFreshnessRows ?? []}
                  options={filterOptions}
                  onFilter={setFilter}
                  onDownload={downloadExcel}
                  downloadDisabled={
                    isLoading || !dataFreshnessRows || dataFreshnessRows.length === 0
                  }
                />
              }
            />
          </div>
        </WidgetContainer>
      </div>
    </Page>
  );
};
